import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Check if Firebase is configured
const isConfigured = Boolean(firebaseConfig.projectId && firebaseConfig.apiKey);

let _app = null;
let _messaging = null;

/** Get Firebase app instance (lazy init) */
function getApp() {
  if (!isConfigured) return null;
  if (_app) return _app;
  _app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
  return _app;
}

/** Get Firebase Messaging instance (only if supported) */
export async function getFirebaseMessaging() {
  if (_messaging) return _messaging;
  const app = getApp();
  if (!app) return null;
  try {
    const supported = await isSupported();
    if (!supported) return null;
    _messaging = getMessaging(app);
    return _messaging;
  } catch (e) {
    console.warn('Firebase Messaging init failed:', e.message);
    return null;
  }
}

/** Request notification permission and get FCM token using main SW */
export async function requestNotificationPermission(vapidKey) {
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return null;

    const messaging = await getFirebaseMessaging();
    if (!messaging) return null;

    // Use the main service worker (sw.js) which has Firebase messaging merged in
    const swRegistration = await navigator.serviceWorker.getRegistration('/');
    const tokenOptions = { serviceWorkerRegistration: swRegistration };
    if (vapidKey) tokenOptions.vapidKey = vapidKey;

    const token = await getToken(messaging, tokenOptions);
    return token;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
}

/** Listen for foreground messages */
export function onForegroundMessage(callback) {
  getFirebaseMessaging().then((messaging) => {
    if (!messaging) return;
    onMessage(messaging, callback);
  });
}

export { isConfigured };
